import { useState, useEffect, useRef } from 'react';

interface Interval {
  id: number;
  startTime: number;
  endTime: number;
  title?: string;
}

/**
 * Custom hook that debounces interval changes for transcription updates
 * Visual updates remain immediate, but transcription calculation is delayed
 * by the specified delay (default 1.5 seconds)
 */
export function useDebouncedIntervals(
  intervals: Interval[],
  delay: number = 1500
): Interval[] {
  const [debouncedIntervals, setDebouncedIntervals] = useState<Interval[]>(intervals);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout to update debounced intervals
    timeoutRef.current = setTimeout(() => {
      setDebouncedIntervals(intervals);
    }, delay);

    // Cleanup timeout on unmount or when intervals change again
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [intervals, delay]);

  // Update immediately if intervals are empty (initial state)
  useEffect(() => {
    if (intervals.length === 0) {
      setDebouncedIntervals(intervals);
    }
  }, [intervals.length]);

  return debouncedIntervals;
}
